import React, { lazy } from 'react';
import { StarIcon, QuoteIcon } from 'lucide-react';
import { Reveal } from './Reveal';
const FEATURED_PHOTO = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=400&q=80';
interface Testimonial {
  photo: string;
  alt: string;
  quote: React.ReactNode;
  name: string;
  role: string;
}
const testimonials: Testimonial[] = [{
  photo: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=200&q=80',
  alt: 'Elena M., dueña de negocio, profesional en una oficina.',
  quote: 'Tengo una agencia de organización de eventos y siempre me costaba explicar qué nos hace diferentes a la competencia. Usé el Offer Clarity GPT y en 10 minutos me sacó un ángulo que nunca se me había ocurrido. No he facturado millones, pero por fin tengo una página web que mis clientes entienden a la primera. Solo por el tiempo que me ahorró en redactar, ya se pagó solo.',
  name: 'Elena M.',
  role: 'Dueña de Negocio'
}, {
  photo: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?auto=format&fit=crop&w=200&q=80',
  alt: 'Javier G., networker y afiliado joven con una laptop.',
  quote: 'Compré Launch Machine porque me gustaron los GPTs para mis propios anuncios, pero la sorpresa fue la licencia de reventa. Compartí mi experiencia en mis historias de Instagram, puse mi link de Hotmart y en 48 horas hice 3 ventas. Básicamente, el sistema me salió gratis y ya tengo mis primeros $70 de ganancia limpia sin haber lanzado nada propio todavía.',
  name: 'Javier G.',
  role: 'Networker y Afiliado'
}, {
  photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=200&q=80',
  alt: 'Ricardo V., consultor independiente de unos 45-50 años sonriendo.',
  quote: 'A mi edad me da un poco de miedo todo esto de la IA y los "prompts". Pensé que sería complicado, pero Launch Machine de verdad es "hablar" con los bots. El Ad Creative me dio los textos para mis anuncios de Facebook y por primera vez en meses el costo por clic me bajó a la mitad. Es directo y fácil de usar, que es lo que yo necesitaba.',
  name: 'Ricardo V.',
  role: 'Consultor Independiente'
}, {
  photo: 'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=200&q=80',
  alt: 'Sofía L., creadora de contenido grabando con un aro de luz de fondo.',
  quote: 'Mi problema era que mis Reels no los veía nadie. Me quedaba sin ideas. El Hook Generator se volvió mi mano derecha; ahora saco 10 ideas de videos en un segundo y la retención de mi audiencia mejoró muchísimo. Sigo siendo yo la que graba, pero saber qué decir al principio del video cambió todo el juego para mi cuenta.',
  name: 'Sofía L.',
  role: 'Creadora de Contenido'
}, {
  photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=200&q=80',
  alt: 'Daniel K., infoproductor trabajando desde su casa.',
  quote: 'Lo que más me gustó fue el Email Launch GPT. Siempre lanzaba mis productos y luego me olvidaba de mandar correos porque me daba pereza escribirlos. El sistema me armó una secuencia de 7 días que se siente muy natural, no parece un robot. Gracias a esos correos, recuperé 4 ventas de personas que habían abandonado el carrito. Pequeños ajustes, grandes resultados.',
  name: 'Daniel K.',
  role: 'Infoproductor'
}];
function Stars({
  size = 'h-4 w-4'
}: {
  size?: string;
}) {
  return <span className="flex" aria-hidden="true" data-id="element-165">
      {Array.from({
      length: 5
    }).map((_, i) => <StarIcon key={i} className={`${size} fill-amber-400 text-amber-400`} data-id="element-166" />)}
    </span>;
}
export function TestimonialSection() {
  return <section className="w-full bg-zinc-950 py-12 sm:py-24" data-id="element-167">
      <div className="mx-auto max-w-6xl px-4" data-id="element-168">
        <Reveal data-id="element-169">
          <h2 className="text-center font-display text-2xl text-white sm:text-4xl" data-id="element-170">
            Lo que dicen los <span className="text-orange-500" data-id="element-171">lanzadores</span>
          </h2>
        </Reveal>

        {/* Featured testimonial */}
        <Reveal delay={0.05} data-id="element-172">
          <div className="mx-auto mt-12 max-w-4xl overflow-hidden rounded-3xl border border-orange-500/20 bg-black/50" data-id="element-173">
            <div className="grid sm:grid-cols-[auto_1fr]" data-id="element-174">
              <div className="flex items-center justify-center bg-zinc-900/60 p-8" data-id="element-175">
                <img src={FEATURED_PHOTO} alt="Carlos R., emprendedor digital sonriente." className="h-32 w-32 rounded-full border-2 border-orange-500/40 object-cover sm:h-40 sm:w-40" loading="lazy" data-id="element-176" />
              </div>
              <div className="p-8 sm:p-10" data-id="element-177">
                <QuoteIcon className="h-8 w-8 text-orange-500/60" aria-hidden="true" data-id="element-178" />
                <blockquote className="mt-4 text-base leading-relaxed text-zinc-200 sm:text-lg" data-id="element-179">
                  Llevaba 6 meses intentando lanzar mi curso de Fitness. Estaba
                  perdido entre embudos y anuncios. Compré Launch Machine un
                  lunes, y para el miércoles ya tenía mi página, mis anuncios y
                  mis emails listos. Lancé el viernes y con solo{' '}
                  <span className="font-bold text-amber-400" data-id="element-180">
                    $10 al día en Meta Ads
                  </span>{' '}
                  conseguí{' '}
                  <span className="font-bold text-amber-400" data-id="element-181">
                    17 ventas en 6 días
                  </span>
                  . Lo mejor es que ahora mis amigos me lo compran y gano el 50%
                  de comisión. ¡Es un negocio redondo!
                </blockquote>
                <div className="mt-6 flex items-center gap-3" data-id="element-182">
                  <Stars data-id="element-183" />
                  <cite className="not-italic text-sm font-semibold text-white" data-id="element-184">
                    Carlos R., Emprendedor Digital
                  </cite>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Secondary testimonials grid */}
        <div className="mt-6 grid gap-5 sm:mt-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-185">
          {testimonials.map((t, i) => <Reveal key={t.name} delay={i % 3 * 0.06} data-id="element-186">
              <figure className="flex h-full flex-col rounded-2xl border border-zinc-800 bg-black/40 p-6" data-id="element-187">
                <QuoteIcon className="h-6 w-6 text-orange-500/50" aria-hidden="true" data-id="element-188" />
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-zinc-300" data-id="element-189">
                  {t.quote}
                </blockquote>
                <Stars size="h-3.5 w-3.5" data-id="element-190" />
                <figcaption className="mt-4 flex items-center gap-3 border-t border-zinc-800 pt-4" data-id="element-191">
                  <img src={t.photo} alt={t.alt} className="h-11 w-11 rounded-full border border-orange-500/30 object-cover" loading="lazy" data-id="element-192" />
                  <span data-id="element-193">
                    <span className="block text-sm font-semibold text-white" data-id="element-194">
                      {t.name}
                    </span>
                    <span className="block text-xs text-amber-400" data-id="element-195">
                      {t.role}
                    </span>
                  </span>
                </figcaption>
              </figure>
            </Reveal>)}
        </div>
      </div>
    </section>;
}